import React from 'react';
import {
  Box,
  Container,
  HStack,
  Button,
  Text,
  Badge,
  IconButton,
  Tooltip,
  Divider,
  VStack,
  Code,
} from '@chakra-ui/react';
import { 
  FiHome, 
  FiSettings, 
  FiRefreshCw,
  FiClock
} from 'react-icons/fi';
import { Link as RouterLink, useLocation } from 'react-router-dom';

function MenuBar({ 
  configData, 
  lastUpdated, 
  summaryData, 
  refreshing, 
  onRefresh, 
  onToggleManagement,
  showManagement 
}) {
  const location = useLocation();

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getWalletUpdateInfo = () => {
    if (!summaryData?.summary) return { displayTime: 'Never', walletInfo: [] };
    
    const walletInfo = [];
    
    // Collect last update info for each wallet
    summaryData.summary.forEach(address => {
      let lastUpdate = null;
      
      if (address.balanceHistory && address.balanceHistory.length > 0) {
        // Get the most recent entry (last in the array)
        const lastEntry = address.balanceHistory[address.balanceHistory.length - 1];
        if (lastEntry && lastEntry.date) {
          lastUpdate = new Date(lastEntry.date).getTime();
        }
      }
      
      walletInfo.push({
        walletName: address.walletName || address.address,
        address: address.address,
        lastUpdate: lastUpdate,
        lastUpdateFormatted: lastUpdate ? formatDate(new Date(lastUpdate).toISOString()) : 'No data'
      });
    });
    
    // Sort by last update time (oldest to newest)
    walletInfo.sort((a, b) => {
      if (!a.lastUpdate && !b.lastUpdate) return 0;
      if (!a.lastUpdate) return 1;
      if (!b.lastUpdate) return -1;
      return a.lastUpdate - b.lastUpdate;
    });
    
    // Find the oldest time for display
    const oldestTime = walletInfo.find(w => w.lastUpdate)?.lastUpdate;
    
    if (!oldestTime) return { displayTime: 'No data', walletInfo };
    
    const now = new Date();
    const diff = now - oldestTime;
    const minutes = Math.floor(diff / (1000 * 60));
    
    let displayTime;
    if (minutes < 1) displayTime = 'Just now';
    else if (minutes < 60) displayTime = `${minutes}m ago`;
    else {
      const hours = Math.floor(minutes / 60);
      if (hours < 24) displayTime = `${hours}h ago`;
      else displayTime = formatDate(new Date(oldestTime).toISOString());
    }
    
    return { displayTime, walletInfo };
  };

  const menuItems = [
    {
      path: '/',
      label: 'Dashboard',
      icon: FiHome,
      colorScheme: 'blue',
      description: 'Monitor wallet balances'
    }
  ];

  return (
    <Box bg="white" borderBottom="1px" borderColor="gray.200" py={2}>
      <Container maxW="full" px={6}>
        <HStack spacing={6} justify="space-between" align="center">
          {/* Main Navigation */}
          <HStack spacing={1}>
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              
              return (
                <Tooltip key={item.path} label={item.description} placement="bottom">
                  <Button
                    as={RouterLink}
                    to={item.path}
                    size="sm"
                    variant={isActive ? 'solid' : 'ghost'}
                    colorScheme={item.colorScheme}
                    leftIcon={<Icon size={16} />}
                    fontWeight={isActive ? 'semibold' : 'normal'}
                    _hover={{
                      bg: isActive ? undefined : `${item.colorScheme}.50`,
                      color: isActive ? undefined : `${item.colorScheme}.600`
                    }}
                    transition="all 0.2s"
                  >
                    {item.label}
                  </Button>
                </Tooltip>
              );
            })}
            
          </HStack>

          {/* Right Side - Status & Actions */}
          <HStack spacing={4}>
            {/* Status Indicators */}
            <HStack spacing={2}>
              <Badge 
                colorScheme={configData?.pagerDutyEnabled ? 'green' : 'red'}
                variant="subtle"
                fontSize="xs"
                px={2}
                py={1}
              >
                PD: {configData?.pagerDutyEnabled ? 'ON' : 'OFF'}
              </Badge>
              <Badge colorScheme="blue" variant="subtle" fontSize="xs" px={2} py={1}>
                Slack: ON
              </Badge>
            </HStack>

            <Divider orientation="vertical" height="20px" />

            {/* Management Toggle (Dashboard only) */}
            {location.pathname === '/' && (
              <Tooltip label="Toggle address management" placement="bottom">
                <Button
                  size="sm"
                  variant={showManagement ? 'solid' : 'outline'}
                  colorScheme="orange"
                  leftIcon={<FiSettings size={16} />}
                  onClick={() => onToggleManagement(!showManagement)}
                  _hover={{
                    bg: showManagement ? undefined : 'orange.50',
                    color: showManagement ? undefined : 'orange.600'
                  }}
                >
                  Manage
                </Button>
              </Tooltip>
            )}

            <Divider orientation="vertical" height="20px" />

            {/* Last Update Info */}
            <HStack spacing={2} color="gray.600">
              <FiClock size={14} />
              {(() => {
                const { displayTime, walletInfo } = getWalletUpdateInfo();
                return (
                  <Tooltip
                    label={
                      <VStack spacing={2} align="start" maxW="400px">
                        <Text fontSize="sm" fontWeight="semibold">Wallet Update Times (Oldest → Newest)</Text>
                        {walletInfo.length === 0 ? (
                          <Text fontSize="xs" color="gray.300">No wallet data available</Text>
                        ) : (
                          walletInfo.map((wallet, index) => (
                            <HStack key={wallet.address} spacing={2} align="start">
                              <Text fontSize="xs" color="gray.400" minW="20px">
                                {index + 1}.
                              </Text>
                              <VStack spacing={0} align="start">
                                <Text fontSize="xs" fontWeight="medium" color="white">
                                  {wallet.walletName}
                                </Text>
                                <Code fontSize="xs" colorScheme="gray" variant="subtle">
                                  {wallet.address.slice(0, 8)}...{wallet.address.slice(-8)}
                                </Code>
                                <Text fontSize="xs" color="gray.300">
                                  {wallet.lastUpdateFormatted}
                                </Text>
                              </VStack>
                            </HStack>
                          ))
                        )}
                      </VStack>
                    }
                    placement="bottom"
                    hasArrow
                    bg="gray.800"
                    color="white"
                    borderRadius="md"
                    p={3}
                  >
                    <Text fontSize="xs" fontWeight="medium" cursor="pointer">
                      {displayTime}
                    </Text>
                  </Tooltip>
                );
              })()}
              <Tooltip label="Refresh data" placement="bottom">
                <IconButton
                  icon={<FiRefreshCw />}
                  onClick={() => onRefresh(true)}
                  isLoading={refreshing}
                  size="xs"
                  variant="ghost"
                  colorScheme="blue"
                  _hover={{ bg: 'blue.50', color: 'blue.600' }}
                />
              </Tooltip>
            </HStack>
          </HStack>
        </HStack>
      </Container>
    </Box>
  );
}

export default MenuBar;
